import React, { useState } from 'react';

export default function PalaceArchwayViewTemplate({ data, onTextDragStart, onTextChange, isDragging }) {
  const textPositions = data.textPositions || {};
  const [editingField, setEditingField] = useState(null);

  const getTextStyle = (field, defaultTop = 80) => ({
    position: 'absolute',
    left: `${textPositions[field]?.x ?? 50}%`,
    top: `${textPositions[field]?.y ?? defaultTop}px`,
    transform: 'translateX(-50%)',
    cursor: isDragging === field ? 'grabbing' : (editingField === field ? 'text' : 'move'),
    userSelect: editingField === field ? 'text' : 'none',
    zIndex: 30,
    pointerEvents: 'auto',
  });

  const handleTextEdit = (field, e) => {
    if (onTextChange) {
      onTextChange(field, e.target.textContent || e.target.innerText);
    }
  };

  const handleMouseDown = (field, e) => {
    // If currently editing this field, don't start drag
    if (editingField === field) {
      return;
    }
    
    // Single click/drag → move text
    onTextDragStart?.(field, e);
  };

  const handleDoubleClick = (field, e) => {
    // Double click enters edit mode
    e.preventDefault();
    e.stopPropagation();
    setEditingField(field);
    // Focus the contentEditable element
    const editableElement = e.currentTarget.querySelector('[contenteditable="true"]');
    if (editableElement) {
      setTimeout(() => {
        editableElement.focus();
        // Select all text for easy editing
        const range = document.createRange();
        range.selectNodeContents(editableElement);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }, 0);
    }
  };

  const handleFocus = (field) => {
    setEditingField(field);
  };

  const handleBlur = (field, e) => {
    setEditingField(null);
    handleTextEdit(field, e);
  };

  return (
    <div className="template-card bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 text-slate-900 rounded-lg overflow-hidden shadow-2xl relative min-h-[600px] flex flex-col">
      <img 
        src="/einvite-templates/palace-archway-view.jpg" 
        alt="Palace Archway View"
        className="absolute inset-0 w-full h-full object-cover z-0"
        style={{ objectPosition: 'center' }}
      />
      
      <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 relative z-20 flex-shrink-0"></div>
      
      <div className="content-area p-8 space-y-6 relative z-10 flex-1 min-h-0">
        <div data-anchor-container className="relative h-[240px] mb-6">
          <div 
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => handleBlur('headerText', e)}
            onFocus={() => handleFocus('headerText')}
            className="text-center text-xs uppercase tracking-widest text-amber-800 opacity-80 mb-6 font-serif cursor-text hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60 min-h-[20px]"
            style={{ outline: editingField === 'headerText' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none' }}
          >
            {data.headerText || 'Together with their families'}
          </div>
          
          <div 
            data-draggable="true"
            data-field="brideName"
            style={getTextStyle('brideName', 80)}
            onMouseDown={(e) => handleMouseDown('brideName', e)}
            onDoubleClick={(e) => handleDoubleClick('brideName', e)}
            className={`absolute ${isDragging === 'brideName' ? 'opacity-50' : ''} transition-opacity hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60`}
            title="Double-click to edit, drag to move"
          >
            <div 
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => handleBlur('brideName', e)}
              onFocus={() => handleFocus('brideName')}
              className="text-5xl font-serif font-bold text-amber-900 tracking-wide drop-shadow-lg cursor-text min-w-[100px] text-center"
              style={{ 
                outline: editingField === 'brideName' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none',
                pointerEvents: editingField === 'brideName' ? 'auto' : 'none'
              }}
            >
              {data.brideName || 'Bride Name'}
            </div>
          </div>
          
          <div 
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => handleBlur('connectorText', e)}
            onFocus={() => handleFocus('connectorText')}
            className="absolute left-1/2 top-[140px] -translate-x-1/2 text-3xl text-amber-700 font-serif cursor-text hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60 min-h-[30px] text-center"
            style={{ outline: editingField === 'connectorText' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none' }}
          >
            {data.connectorText || '&'}
          </div>
          
          <div 
            data-draggable="true"
            data-field="groomName"
            style={getTextStyle('groomName', 180)}
            onMouseDown={(e) => handleMouseDown('groomName', e)}
            onDoubleClick={(e) => handleDoubleClick('groomName', e)}
            className={`absolute ${isDragging === 'groomName' ? 'opacity-50' : ''} transition-opacity hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60`}
            title="Double-click to edit, drag to move"
          >
            <div 
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => handleBlur('groomName', e)}
              onFocus={() => handleFocus('groomName')}
              className="text-5xl font-serif font-bold text-amber-900 tracking-wide drop-shadow-lg cursor-text min-w-[100px] text-center"
              style={{ 
                outline: editingField === 'groomName' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none',
                pointerEvents: editingField === 'groomName' ? 'auto' : 'none'
              }}
            >
              {data.groomName || 'Groom Name'}
            </div>
          </div>
        </div>

        <div data-anchor-container className="relative h-[180px] mt-8 bg-white/60 rounded-lg p-5 border-2 border-amber-200 backdrop-blur-sm">
          <div 
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => handleBlur('requestText', e)}
            onFocus={() => handleFocus('requestText')}
            className="text-xs uppercase tracking-widest text-amber-800 opacity-80 font-serif text-center mb-4 cursor-text hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60 min-h-[20px]"
            style={{ outline: editingField === 'requestText' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none' }}
          >
            {data.requestText || 'Request the pleasure of your company'}
          </div>
          
          <div 
            data-draggable="true"
            data-field="weddingDate"
            style={getTextStyle('weddingDate', 60)}
            onMouseDown={(e) => handleMouseDown('weddingDate', e)}
            onDoubleClick={(e) => handleDoubleClick('weddingDate', e)}
            className={`absolute ${isDragging === 'weddingDate' ? 'opacity-50' : ''} transition-opacity hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60`}
            title="Double-click to edit, drag to move"
          >
            <div 
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => handleBlur('weddingDate', e)}
              onFocus={() => handleFocus('weddingDate')}
              className="text-2xl font-serif text-amber-900 font-bold drop-shadow-lg cursor-text min-w-[150px] text-center"
              style={{ 
                outline: editingField === 'weddingDate' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none',
                pointerEvents: editingField === 'weddingDate' ? 'auto' : 'none'
              }}
            >
              {data.weddingDate || 'Wedding Date'}
            </div>
          </div>
          
          <div 
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => handleBlur('weddingTime', e)}
            onFocus={() => handleFocus('weddingTime')}
            className="absolute left-1/2 top-[100px] -translate-x-1/2 text-lg text-amber-800 font-serif drop-shadow cursor-text hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60 min-h-[20px] text-center"
            style={{ outline: editingField === 'weddingTime' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none' }}
          >
            {data.weddingTime || ''}
          </div>
          
          <div 
            data-draggable="true"
            data-field="venue"
            style={getTextStyle('venue', 130)}
            onMouseDown={(e) => handleMouseDown('venue', e)}
            onDoubleClick={(e) => handleDoubleClick('venue', e)}
            className={`absolute ${isDragging === 'venue' ? 'opacity-50' : ''} transition-opacity hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60`}
            title="Double-click to edit, drag to move"
          >
            <div 
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => handleBlur('venue', e)}
              onFocus={() => handleFocus('venue')}
              className="text-lg font-bold text-amber-900 font-serif drop-shadow-lg cursor-text min-w-[150px] text-center"
              style={{ 
                outline: editingField === 'venue' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none',
                pointerEvents: editingField === 'venue' ? 'auto' : 'none'
              }}
            >
              {data.venue || 'Venue'}
            </div>
            <div 
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => handleBlur('venueAddress', e)}
              onFocus={() => handleFocus('venueAddress')}
              className="text-sm text-amber-700 font-serif mt-1 drop-shadow cursor-text min-w-[200px] text-center"
              style={{ 
                outline: editingField === 'venueAddress' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none',
                pointerEvents: editingField === 'venueAddress' ? 'auto' : 'none'
              }}
            >
              {data.venueAddress || ''}
            </div>
          </div>
        </div>

        <div data-anchor-container className="relative h-[80px] my-4">
          <div 
            data-draggable="true"
            data-field="quote"
            style={getTextStyle('quote', 40)}
            onMouseDown={(e) => handleMouseDown('quote', e)}
            onDoubleClick={(e) => handleDoubleClick('quote', e)}
            className={`absolute ${isDragging === 'quote' ? 'opacity-50' : ''} transition-opacity hover:outline hover:outline-1 hover:outline-dashed hover:outline-pink-400/60 text-center italic text-amber-700 text-base py-4 border-y-2 border-amber-300 font-serif drop-shadow`}
            title="Double-click to edit, drag to move"
          >
            <div 
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => handleBlur('quote', e)}
              onFocus={() => handleFocus('quote')}
              className="cursor-text min-w-[200px]"
              style={{ 
                outline: editingField === 'quote' ? '2px dashed rgba(244, 114, 182, 0.8)' : 'none',
                pointerEvents: editingField === 'quote' ? 'auto' : 'none'
              }}
            >
              {data.quote ? `"${data.quote}"` : 'Quote'}
            </div>
          </div>
        </div>
      </div>

      <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 relative z-20 mt-auto flex-shrink-0"></div>
    </div>
  );
}

